import { ChangeDetectionStrategy, Component, computed, inject, input, viewChild } from '@angular/core';
import {
  InspectionEntryFormType,
  RISK_LEVEL_OPTIONS,
  usesRiskAssessmentSignal,
} from '../../inspection-util';
import { BehaviorSubject, firstValueFrom, map, startWith, switchMap } from 'rxjs';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { NbDialogService } from '@nebular/theme';
import { TextBlockItemDto, TextBlockService } from '@artemis-software/wr-api';
import { InspectionFormWrapper } from '../../inspection-form';
import {
  MultiAttachmentControlComponent,
} from '@shared/form-controls/attachment-upload/multi-attachment-control/multi-attachment-control.component';
import {
  TextBlockSelectDialogComponent,
} from '@components/dialog/text-block-select-dialog/text-block-select-dialog.component';
import { watch } from '@/utils/watch';
import { asyncComputed } from '@/utils/signals/asyncComputed';

@Component({
  standalone: false,
  selector: 'wr-inspection-entry-form',
  templateUrl: './inspection-entry-form.component.html',
  styleUrls: ['./inspection-entry-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectionEntryFormComponent {

  readonly attachmentUpload = viewChild(MultiAttachmentControlComponent);

  readonly inspectionEntry = input.required<InspectionEntryFormType>();
  readonly formWrapper = input.required<InspectionFormWrapper>();
  readonly enabled = computed(() => this.formWrapper().enabled());

  readonly riskLevelOptions = RISK_LEVEL_OPTIONS;
  readonly loading$ = new BehaviorSubject<boolean>(false);

  readonly hasAnyPendingAttachments = computed(() => this.attachmentUpload()?.hasAnyPendingAttachments ?? false);

  constructor() {
    watch(this.enabled, (enabled) => {
      if (enabled) {
        this.inspectionEntry().enable({ emitEvent: false });
      } else {
        this.inspectionEntry().disable({ emitEvent: false });
      }
    });
  }

  readonly creatorTenantId = computed(() => this.formWrapper().value()?.creatorTenant?.id);
  readonly usesRiskAssessment = usesRiskAssessmentSignal(this.formWrapper);
  readonly predefinedAssessment = computed(() => this.inspectionEntry().value.predefinedAssessment);

  private readonly nbDialogService = inject(NbDialogService);
  private readonly textBlockService = inject(TextBlockService);

  private readonly selectedAssessmentPresetId = toSignal(
    toObservable(this.inspectionEntry).pipe(
      switchMap(entry => entry.controls.predefinedAssessment.valueChanges.pipe(
        startWith(entry.controls.predefinedAssessment.value),
      )),
      map(preset => preset?.id),
    ),
  );

  readonly textBlocks = asyncComputed<TextBlockItemDto[]>(async () => {
    // Im Lesemodus rendert das Template den Button nie — der Request wäre pro
    // geöffneter Prüfzeile ein sinnloser POST.
    if (!this.enabled()) {
      return [];
    }
    const assessmentPresetId = this.selectedAssessmentPresetId();
    if (!assessmentPresetId) {
      return [];
    }
    const textBlocks = await firstValueFrom(this.textBlockService.findAll({ assessmentPresetId }));
    return [...textBlocks].sort((a, b) => a.ranking - b.ranking);
  }, []);

  async insertTextBlock(): Promise<void> {
    const ref = this.nbDialogService.open(TextBlockSelectDialogComponent, {
      context: { textBlocks: this.textBlocks() },
    });
    const textBlock = await firstValueFrom(ref.onClose) as TextBlockItemDto | undefined | false;
    if (!textBlock) {
      return;
    }
    const noteControl = this.inspectionEntry().controls.note;
    const currentNote = (noteControl.value ?? '').trim();
    noteControl.setValue(currentNote ? `${ currentNote }\n\n${ textBlock.text }` : textBlock.text);
    noteControl.markAsDirty();
  }

  readonly recommendedDeadline = computed(() => this.predefinedAssessment()?.checkInterval);
  readonly recommendedAction = computed(() => this.predefinedAssessment()?.action);
  readonly building = computed(() => this.formWrapper().value()?.building);
  readonly buildingSection = computed(() => {
    const id = this.inspectionEntry().value.buildingSectionId;
    return this.building()?.sections?.find(s => s.id === id);
  });
}
